import isEqual from "lodash-es/isEqual";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import SpaceSelector from "components/SpaceSelector";
import SharedModulesTierInfo from "components/TierInfo/SharedModulesTierInfo";
import Box from "ds/components/Box";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import useTypedContext from "hooks/useTypedContext";
import useAllowedSpaceToShareModule from "shared/Module/access/useAllowedSpaceToShareModule";
import { BillingTierFeature } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import useTierFeature from "views/Account/hooks/useTierFeature";

import { ModuleFormContext } from "../context";
import NewModuleFooter from "../Footer";
import { ModuleCreationWizardStep, ModuleShareNewFormFields } from "../types";
import Documentation from "./Documentation";
import { useShareModule } from "./useShareModule";

const ModuleShareNew = () => {
  const spaces = useAllowedSpaceToShareModule();
  const { currentStep, updateStepData, formData, createdModuleId, goToNextStep, goToSummary } =
    useTypedContext(ModuleFormContext);
  const stepData = formData[ModuleCreationWizardStep.Share];
  const spaceDetails = formData[ModuleCreationWizardStep.Vcs].space;

  const builderForm = useForm<ModuleShareNewFormFields>({
    defaultValues: {
      ...stepData,
    },
    mode: "onChange",
  });

  const { watch, setValue } = builderForm;

  const isActive = useTierFeature(BillingTierFeature.PrivateModuleSharing);

  const newStepData = watch();
  const spaceFields = watch("sharedSpaces");

  const { shareModule, loading } = useShareModule({
    moduleId: createdModuleId,
  });

  const selectedSpaceIds = useMemo(
    () => new Set(spaceFields?.map((space) => space.value).filter((id) => id)),
    [spaceFields]
  );

  const handleNextStep = () => {
    if (isActive) updateStepData(currentStep, newStepData);
    goToNextStep();
  };

  const handleSkipToSummary = () => {
    if (isActive) updateStepData(currentStep, newStepData);
    goToSummary();
  };

  const handleShare = (shouldGoToSummary?: boolean) => {
    shareModule(newStepData, shouldGoToSummary ? handleSkipToSummary : handleNextStep);
  };

  const handleSelectionChange = useCallback(
    (data: Set<string>) => {
      setValue(
        "sharedSpaces",
        [...data].map((id) => ({ value: id }))
      );
    },
    [setValue]
  );

  const isDataChanged = stepData.sharedAccounts && !isEqual(newStepData, stepData);

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Set availability (optional)"
          description="Your module is automatically available in its origin space and all descendants with inheritance enabled. Expand availability to other spaces as needed."
        />

        <Box direction="column" gap="x-large" fullWidth>
          <SharedModulesTierInfo type="banner" />

          <FormProvider {...builderForm}>
            <SpaceSelector
              spaces={spaces}
              selectedSpaceIds={selectedSpaceIds}
              onSelectionChange={handleSelectionChange}
              defaultSelection={spaceDetails}
            />
          </FormProvider>
        </Box>
      </FullScreenModalBody>

      <NewModuleFooter
        isDataChanged={!!isDataChanged}
        documentationLink={getDocsUrl("/vendors/terraform/module-registry#module-availability")}
        documentationTitle="Sharing modules"
        documentationBody={<Documentation />}
        submitCallback={isActive ? handleShare : undefined}
        loading={loading}
      />
    </>
  );
};

export default ModuleShareNew;
